import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TAX_MODES } from "@/lib/constants";
import { fmtDate, fmtDateTime, fmtRupiah } from "@/lib/format";
import { DealAddendums } from "@/components/DealAddendums";
import { toast } from "sonner";
import { Lock, Unlock, Pencil, Loader2, History } from "lucide-react";

function Stat({ label, value, testid, strong }) {
  return (
    <div className={`rounded-md border p-3 ${strong ? "border-primary bg-red-50" : ""}`}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`font-bold ${strong ? "text-primary text-lg" : "text-navy"}`} data-testid={testid}>{value}</div>
    </div>
  );
}

function DealForm({ open, onClose, deal, onSaved, projectId }) {
  const sug = deal?.suggestion;
  const [amount, setAmount] = useState("");
  const [useEst, setUseEst] = useState(false);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [taxMode, setTaxMode] = useState("");
  const [ppn, setPpn] = useState("");
  const needPpn = taxMode === "Termasuk PPN" || taxMode === "Belum Termasuk PPN";
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) { return; }
    const fromEst = !deal?.exists && !!sug;
    setAmount(deal?.exists ? String(deal.harga_deal) : sug ? String(sug.grand_total) : "");
    setUseEst(deal?.exists ? !!deal.estimate_id : fromEst);
    setDate(deal?.deal_date || new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" }));
    setNote(deal?.note || ""); setReason("");
    setTaxMode(deal?.tax_mode || ""); setPpn(deal?.ppn_percent != null ? String(deal.ppn_percent) : "");
  }, [open, deal, sug]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/projects/${projectId}/deal`, {
        harga_deal: Number(amount), estimate_id: useEst && sug ? (deal?.estimate_id || sug.estimate_id) : null,
        deal_date: date, note, reason, tax_mode: taxMode || null, ppn_percent: needPpn ? Number(ppn) : null,
      });
      toast.success("Harga Deal disimpan"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="deal-form-dialog">
        <DialogHeader><DialogTitle>{deal?.exists ? "Ubah Harga Deal" : "Tetapkan Harga Deal"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {sug && (
            <button type="button" data-testid="deal-use-estimate" onClick={() => { setAmount(String(sug.grand_total)); setUseEst(true); }}
              className="w-full text-left text-xs border rounded-md p-2 hover:border-primary">
              Ambil dari Penawaran disetujui <span className="font-mono-k3 text-primary">{sug.estimate_no}</span>: <b>{fmtRupiah(sug.grand_total)}</b>
            </button>
          )}
          <div><label className="text-xs text-muted-foreground">Harga Deal (Rp)</label>
            <Input type="number" min="0" value={amount} data-testid="deal-amount-input" onChange={(e) => { setAmount(e.target.value); if (sug && Number(e.target.value) !== sug.grand_total) setUseEst(false); }} /></div>
          <div className="text-[11px] text-muted-foreground" data-testid="deal-source-hint">Sumber: {useEst ? `Penawaran ${sug?.estimate_no || deal?.estimate_no || ""}` : "Input manual"}</div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2"><label className="text-xs text-muted-foreground">Status Pajak (sesuai kontrak)</label>
              <Select value={taxMode || "none"} onValueChange={(v) => setTaxMode(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="deal-tax-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">BELUM DITENTUKAN</SelectItem>
                  {TAX_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><label className="text-xs text-muted-foreground">Tarif PPN (%)</label>
              <Input type="number" min="0" step="0.01" value={ppn} disabled={!needPpn} data-testid="deal-ppn-input" onChange={(e) => setPpn(e.target.value)} /></div>
          </div>
          <div><label className="text-xs text-muted-foreground">Tanggal Kesepakatan</label>
            <Input type="date" value={date} data-testid="deal-date-input" onChange={(e) => setDate(e.target.value)} /></div>
          <div><label className="text-xs text-muted-foreground">Catatan</label>
            <Textarea rows={2} value={note} data-testid="deal-note-input" onChange={(e) => setNote(e.target.value)} /></div>
          {deal?.locked && (
            <div><label className="text-xs text-primary font-medium">Alasan perubahan (wajib, harga sudah terkunci)</label>
              <Textarea rows={2} value={reason} data-testid="deal-reason-input" onChange={(e) => setReason(e.target.value)} /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="deal-cancel-btn">Batal</Button>
          <Button className="bg-primary hover:bg-red-700" disabled={busy || !amount || (needPpn && !ppn) || (deal?.locked && !reason.trim())} onClick={save} data-testid="deal-save-btn">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />} Simpan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const DealCard = ({ projectId, onChanged }) => {
  const [deal, setDeal] = useState(null);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/projects/${projectId}/deal`); setDeal(data); setErr(""); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || "Gagal memuat Harga Deal"); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged && onChanged(); };

  const lock = async () => {
    if (!window.confirm(`Kunci Harga Deal ${fmtRupiah(deal.harga_deal)}? Setelah dikunci, perubahan hanya oleh Owner/Manager dengan alasan.`)) return;
    try { await api.post(`/projects/${projectId}/deal/lock`); toast.success("Harga Deal dikunci"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (err && !deal) return <Card className="p-6 text-sm text-primary" data-testid="deal-error">{err}</Card>;
  if (!deal) return <Card className="p-6 flex justify-center"><Loader2 className="animate-spin text-primary" /></Card>;
  return (
    <div className="space-y-3" data-testid="deal-card">
      <Card className="p-4">
        <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="font-heading font-semibold text-navy text-sm">Harga Deal Proyek</h3>
            <p className="text-xs text-muted-foreground">Nilai kontrak tunggal per proyek (berlaku untuk seluruh SPK/RO). Dasar invoice, DP & piutang.</p>
          </div>
          <span data-testid="deal-lock-status" className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${deal.locked ? "bg-emerald-100 text-emerald-800" : deal.exists ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
            {deal.locked ? <Lock size={12} /> : <Unlock size={12} />} {deal.locked ? "DEAL TERKUNCI" : deal.exists ? "BELUM DEAL (draft, belum dikunci)" : "BELUM DEAL"}
          </span>
        </div>
        {!deal.exists ? (
          <p className="text-sm text-muted-foreground mb-3" data-testid="deal-empty">Belum ada Harga Deal.{deal.suggestion ? ` Penawaran disetujui ${deal.suggestion.estimate_no}: ${fmtRupiah(deal.suggestion.grand_total)}.` : " Belum ada penawaran yang disetujui."}</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Stat label="Harga Deal" value={fmtRupiah(deal.harga_deal)} testid="deal-amount" />
            <Stat label="Addendum Disetujui" value={fmtRupiah(deal.addendum_approved_total)} testid="deal-addendum-total" />
            <Stat label="Total Nilai Kontrak" value={fmtRupiah(deal.contract_total)} testid="deal-contract-total" strong />
            <Stat label="Sisa Belum Ditagih" value={fmtRupiah(deal.outstanding)} testid="deal-outstanding" />
          </div>
        )}
        {deal.exists && (
          <div className="text-xs text-muted-foreground space-y-0.5 mb-3" data-testid="deal-meta">
            <div>Sumber: {deal.source === "Penawaran" ? `Penawaran ${deal.estimate_no}` : "Input manual"} · Tanggal deal: {fmtDate(deal.deal_date)}</div>
            <div data-testid="deal-tax-info">Pajak: {deal.tax_mode ? `${deal.tax_mode}${deal.ppn_percent ? ` ${deal.ppn_percent}%` : ""}` : "BELUM DITENTUKAN"}{deal.revenue_dpp != null ? ` · Pendapatan sebelum PPN: ${fmtRupiah(deal.revenue_dpp)} · PPN: ${fmtRupiah(deal.ppn_amount)}` : ""}</div>
            {deal.locked && <div>Dikunci oleh {deal.locked_by} · {fmtDateTime(deal.locked_at)}</div>}
            {deal.note && <div>Catatan: {deal.note}</div>}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {deal.can_edit && <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="deal-edit-btn"><Pencil size={14} className="mr-1.5" />{deal.exists ? "Ubah Harga Deal" : "Tetapkan Harga Deal"}</Button>}
          {deal.can_lock && deal.exists && !deal.locked && <Button size="sm" className="bg-primary hover:bg-red-700" onClick={lock} data-testid="deal-lock-btn"><Lock size={14} className="mr-1.5" />Kunci Harga Deal</Button>}
          {!deal.can_edit && deal.locked && <span className="text-xs text-muted-foreground self-center" data-testid="deal-locked-note">Terkunci. Perubahan hanya oleh Owner/Manager.</span>}
          {deal.exists && !deal.locked && !deal.can_lock && <span className="text-xs text-amber-700 self-center">Menunggu dikunci oleh Owner/Manager.</span>}
        </div>
      </Card>
      {deal.exists && <DealAddendums deal={deal} projectId={projectId} onChanged={refresh} />}
      {deal.history?.length > 0 && (
        <Card className="p-4">
          <h3 className="font-heading font-semibold text-navy text-sm mb-2 flex items-center gap-2"><History size={15} /> Riwayat Harga Deal</h3>
          <div className="space-y-2">
            {deal.history.slice().reverse().map((h, i) => (
              <div key={i} className="text-xs border-b last:border-0 pb-1.5" data-testid={`deal-history-${i}`}>
                <span className="font-medium">{h.action}</span>
                {h.before?.harga_deal != null && <span> · {fmtRupiah(h.before.harga_deal)} →</span>}
                {h.after?.harga_deal != null && <span> {fmtRupiah(h.after.harga_deal)}</span>}
                {h.after?.amount != null && <span> · {fmtRupiah(h.after.amount)}</span>}
                <div className="text-muted-foreground">{fmtDateTime(h.at)} · {h.by} ({h.role}){h.reason ? ` · Alasan: ${h.reason}` : ""}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
      <DealForm open={open} onClose={() => setOpen(false)} deal={deal} projectId={projectId} onSaved={refresh} />
    </div>
  );
};
